// src/components/Detail/Detail.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

// Contexto y servicios
import { useCart } from '../../context/CartContext';
import apiService from '../../services/apiService'; // Asumimos que moviste el servicio a su propio archivo

// Componentes Hijos (Nuestros "Bloques de Canva")
import DetailHeader from './DetailHeader';
import ImageGallery from './ImageGallery';
import ProductInfo from './ProductInfo';
import VariantSelectors from './VariantSelectors';
import PurchaseActions from './PurchaseActions';
import DetailLoading from '../DetailLoading/DetailLoading'; // Tu componente de carga
import './Detail.css';

export default function Detail() {
    const { idProducto } = useParams();
    const { addToCart } = useCart();

    // --- ESTADO PRINCIPAL ---
    const [producto, setProducto] = useState(null);
    const [colores, setColores] = useState([]);
    const [contacto, setContacto] = useState({});
    const [loading, setLoading] = useState(true);

    // --- ESTADO DE SELECCIÓN ---
    const [colorSeleccionado, setColorSeleccionado] = useState(null);
    const [tallaSeleccionada, setTallaSeleccionada] = useState(null);
    const [cantidad, setCantidad] = useState(1);

    // --- ESTADO PARA GALERÍA UNIFICADA ---
    const [todasImagenes, setTodasImagenes] = useState([]);
    const [imagenActual, setImagenActual] = useState(0);

    // --- CARGA DE DATOS ---
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const [productData, contactData] = await Promise.all([
                    apiService.fetchProductDetail(idProducto),
                    apiService.fetchContact()
                ]);

                if (productData.producto) {
                    setProducto(productData.producto);
                    setColores(productData.colores || []);
                    setContacto(contactData);

                    // Crear galería unificada con todas las imágenes
                    let imagenesUnificadas = [];
                    if (productData.colores?.length > 0) {
                        productData.colores.forEach((color, colorIndex) => {
                            if (color.imagenes?.length > 0) {
                                color.imagenes.forEach((imagen, imgIndex) => {
                                    imagenesUnificadas.push({
                                        ...imagen,
                                        colorIndex,
                                        colorId: color.idProductoColor,
                                        color: color.color,
                                        colorHex: color.colorHex,
                                        originalIndex: imgIndex
                                    });
                                });
                            }
                        });
                        setTodasImagenes(imagenesUnificadas);

                        // Selección inicial
                        const firstColor = productData.colores[0];
                        setColorSeleccionado(firstColor);
                        const firstAvailableSize = firstColor.tallas?.find(t => t.stock > 0);
                        setTallaSeleccionada(firstAvailableSize || null);
                        setImagenActual(0);
                    }
                }
            } catch (error) {
                console.error('Error al cargar los datos del producto:', error);
            } finally {
                setLoading(false);
            }
        };
        loadData();
        window.scrollTo(0, 0);
    }, [idProducto]);
    
    // --- MANEJADORES DE EVENTOS (Callbacks) ---
    const handleColorChange = useCallback((color) => {
        setColorSeleccionado(color);
        const firstAvailableSize = color.tallas?.find(t => t.stock > 0);
        setTallaSeleccionada(firstAvailableSize || null);
        setCantidad(1);

        // Encontrar la primera imagen del color seleccionado
        const primeraImagenDelColor = todasImagenes.findIndex(img => img.colorId === color.idProductoColor);
        if (primeraImagenDelColor !== -1) {
            setImagenActual(primeraImagenDelColor);
        }
    }, [todasImagenes]);

    const handleTallaChange = useCallback((talla) => {
        setTallaSeleccionada(talla);
        setCantidad(1);
    }, []);

    // ELIMINADO: La navegación de imágenes ya no cambia automáticamente el color seleccionado

// Dentro de tu componente Detail...

  const handleAddToCart = useCallback(() => {
    const itemToAdd = {
        idProducto: producto.idProducto,
        idProductoColor: colorSeleccionado.idProductoColor,
        idProductoTalla: tallaSeleccionada.idProductoTalla,
        titulo: producto.titulo,
        color: colorSeleccionado.color,
        colorCode: colorSeleccionado.colorHex, 
        talla: tallaSeleccionada.talla,
        precio: producto.precio,
        imagen: colorSeleccionado.imagenes[0]?.imagen || producto.imagen_principal || null,
        cantidad: cantidad,
        stockDisponible: tallaSeleccionada.stock
    };
    addToCart(itemToAdd);
  }, [producto, colorSeleccionado, tallaSeleccionada, cantidad, addToCart]);
    // --- RENDERIZADO ---
    if (loading || !producto) {
        return <DetailLoading />;
    }

    return (
        <div className="modern-detail">
            <DetailHeader producto={producto} />

            <div className="modern-product-container">
                <ImageGallery
                    imagenes={todasImagenes}
                    titulo={producto.titulo}
                    imagenActual={imagenActual}
                    onImagenChange={setImagenActual}
                    colorSeleccionado={colorSeleccionado}
                    onColorChange={handleColorChange}
                    colores={colores}
                />

                <div className="modern-product-info-wrapper">
                    <ProductInfo producto={producto} />
                    
                    <VariantSelectors 
                        colores={colores}
                        colorSeleccionado={colorSeleccionado}
                        onColorChange={handleColorChange}
                        tallasDisponibles={colorSeleccionado?.tallas || []}
                        tallaSeleccionada={tallaSeleccionada}
                        onTallaChange={handleTallaChange}
                    />

                    <PurchaseActions
                        tallaSeleccionada={tallaSeleccionada}
                        cantidad={cantidad}
                        onQuantityChange={setCantidad}
                        onAddToCart={handleAddToCart}
                        contacto={contacto}
                        producto={producto}
                        colorSeleccionado={colorSeleccionado}
                    />
                </div>
            </div>
        </div>
    );
}
